import { useFormContext, useWatch } from 'react-hook-form';
import { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { Pencil } from 'lucide-react';
import { QuestionData, FormData, FormFieldValue } from './types';
import {
  getDependencyChain,
  isAnsweredValue,
  isQuestionVisibleTransitive,
} from '@/hooks/submission_forms/questionVisibility';
import { getFieldComponent } from './fieldDefinitions';
import ClarificationIcon from '@/components/clarifications/ClarificationIcon';
import { useSubmissionFormContext } from './SubmissionFormContext';
import { useAssistFeatures } from './assist/useAssistFeatures';
import { AssistAddons } from './assist/AssistAddons';
import { ScopedValueField } from './fields/ScopedValueField';
import { useClarificationsContext } from '@/contexts/ClarificationsContext';
import { useDebugRole } from '@/contexts/DebugRoleContext';
import { Button } from '@/components/ui/button';
import { isClarificationsActiveStatus } from '@/utils/clarificationStatus';
import { renderPromptText } from './renderPromptText';

/**
 * Hook to determine if a field should be shown based on its dependencies.
 *
 * Visibility is transitive: every question in the depends_on chain must have
 * its own condition met, so a stale answer left on a hidden parent can never
 * reveal its children. Only the chain's parent slugs are watched, so a field
 * re-renders on changes to its ancestors — not on every keystroke in the form.
 */
const useShouldShow = (question: QuestionData) => {
  const { questionsBySlug } = useSubmissionFormContext();

  const watchedSlugs = useMemo(
    () =>
      getDependencyChain(question, questionsBySlug)
        .map(q => q.depends_on?.question_slug)
        .filter((slug): slug is string => Boolean(slug)),
    [question, questionsBySlug]
  );
  const watchedValues = useWatch({ name: watchedSlugs }) as FormFieldValue[];

  if (watchedSlugs.length === 0) return true;

  const chainValues = Object.fromEntries(
    watchedSlugs.map((slug, i) => [slug, watchedValues[i]])
  );
  return isQuestionVisibleTransitive(question, chainValues, questionsBySlug);
};

interface FormFieldProps {
  question: QuestionData;
  disabled?: boolean;
}

export const FormField = ({ question, disabled }: FormFieldProps) => {
  const {
    formState: { errors },
    setValue,
    getValues,
  } = useFormContext<FormData>();

  // Get submission data from context (shared, no duplicate fetches)
  const { submissionFormData, submissionId } = useSubmissionFormContext();
  const { answerAssist, verifyAnswer, publicCollections } = useAssistFeatures();

  // Answer Assist family: text-ish questions opt in per dial in question.meta.
  // Every gate off -> this component renders exactly as it always has.
  const assistableKind =
    question.kind === 'text' || question.kind === 'textarea';
  // The suggestion combobox renders a single-line input, so it only replaces a
  // single-line text field — a textarea keeps its multi-line editor (and still
  // harvests + can carry verify/lookup addons).
  const useScopedField =
    answerAssist &&
    question.kind === 'text' &&
    !!submissionId &&
    (question.meta?.value_scope === 'study' ||
      question.meta?.value_scope === 'study_site');
  const verifyEnabled =
    verifyAnswer &&
    assistableKind &&
    !!submissionId &&
    !!question.meta?.verify_scope &&
    question.meta.verify_scope !== 'off';
  const lookupEnabled =
    publicCollections &&
    assistableKind &&
    !!submissionId &&
    !!question.meta?.public_source;
  const addonsActive = verifyEnabled || lookupEnabled;
  const {
    getCountForQuestion,
    editAnswer,
    canAddClarifications,
    canReplyOnly,
    clarificationRoundState,
  } = useClarificationsContext();
  const { clarificationsEnabled } = useDebugRole();

  // Check if field should be visible
  const visible = useShouldShow(question);
  // null until the first visibility effect runs (i.e. "just mounted")
  const wasVisibleRef = useRef<boolean | null>(null);

  // Determine if clarification icon should be shown, using backend permission
  // flags as the single source of truth:
  //   canAddClarifications  – admin can create new threads on any field
  //   canReplyOnly          – customer while the round is requested can reply
  // Non-admins only see icons on fields that already have clarification threads.
  const hasClarificationsForQuestion = getCountForQuestion(question.slug) > 0;
  const shouldShowClarificationIcon =
    clarificationsEnabled &&
    !!submissionId &&
    !!submissionFormData &&
    isClarificationsActiveStatus(submissionFormData.status) &&
    (canAddClarifications ||
      ((canReplyOnly || clarificationRoundState === 'responded') &&
        hasClarificationsForQuestion));

  // When the form is otherwise disabled (submitted), submission managers unlock
  // fields only while the clarification round is +requested+.
  const customerPendingClarificationReply =
    canReplyOnly && clarificationRoundState === 'requested';
  const isEditableViaClarification =
    !!disabled && customerPendingClarificationReply;
  const effectiveDisabled = disabled && !isEditableViaClarification;
  // SaveService skips file/upload; those use documents flow instead.
  const showClarificationSaveRow =
    isEditableViaClarification &&
    question.kind !== 'file' &&
    question.kind !== 'upload';

  // Track save state for clarification-enabled edits
  const [isSavingEdit, setIsSavingEdit] = useState(false);
  const [editSaved, setEditSaved] = useState(false);
  const [editError, setEditError] = useState<string | null>(null);

  const handleSaveEdit = useCallback(async () => {
    if (isSavingEdit) return;
    setIsSavingEdit(true);
    setEditSaved(false);
    setEditError(null);
    try {
      const currentValue = getValues(question.slug as keyof FormData);
      await editAnswer(question.slug, String(currentValue ?? ''));
      setEditSaved(true);
      // Reset the "saved" indicator after a short delay
      setTimeout(() => setEditSaved(false), 2000);
    } catch (err) {
      // Must surface: a swallowed failure here is indistinguishable from a
      // successful save (the button simply returns to idle), so the user walks
      // away believing a lost edit was recorded. The error persists until the
      // next attempt rather than auto-clearing like the success indicator.
      console.error('Failed to save edit:', err);
      setEditError(
        err instanceof Error && err.message
          ? err.message
          : 'Could not save your change. Please try again.'
      );
    } finally {
      setIsSavingEdit(false);
    }
  }, [editAnswer, getValues, isSavingEdit, question.slug]);

  // Clear the form value when the field becomes (or loads) invisible.
  // - visible → hidden: the user changed a gating answer; clear as before.
  // - hidden at mount with an answer: stale data from before the branch was
  //   closed (or from a previous session). Clear it locally so it can't
  //   resurface if the branch is re-revealed — the autosave will then send an
  //   explicit clear to the server. Skipped when the form is read-only.
  useEffect(() => {
    if (!visible) {
      const wasVisible = wasVisibleRef.current;
      const staleAtMount =
        wasVisible === null &&
        !disabled &&
        isAnsweredValue(getValues(question.slug as keyof FormData));
      if (wasVisible === true || staleAtMount) {
        setValue(question.slug, '', { shouldDirty: true });
      }
    }
    wasVisibleRef.current = visible;
  }, [visible, question.slug, setValue, getValues, disabled]);

  // Don't render if field is not visible
  if (!visible) return null;

  // Get field error
  const fieldError = errors[question.slug as keyof FormData];

  // Get the field component from the new field system
  const FieldComponent = getFieldComponent(question.kind);

  // Check if this is a conditional question
  const isConditional = !!question.depends_on;

  const fieldContent = (
    <div className="space-y-2">
      <label
        htmlFor={question.slug}
        className="text-sm font-normal text-gray-900 whitespace-pre-wrap"
      >
        {renderPromptText(
          question.prompt_template ?? question.prompt,
          question.meta
        )}
        {question.required && <span className="text-red-500 ml-1">*</span>}
      </label>

      {(() => {
        const field = useScopedField ? (
          <ScopedValueField
            question={question}
            submissionId={submissionId ?? ''}
            placeholder={question.meta?.placeholder}
            disabled={effectiveDisabled}
          />
        ) : (
          <FieldComponent
            question={question}
            placeholder={question.meta?.placeholder}
            disabled={effectiveDisabled}
          />
        );
        // Only interpose the addon wrapper when an addon is actually active —
        // otherwise render the field exactly as before (no extra DOM node, no
        // blur handler) so unrelated fields are byte-for-byte unchanged.
        return addonsActive ? (
          <AssistAddons
            question={question}
            submissionId={submissionId ?? ''}
            verifyEnabled={verifyEnabled}
            lookupEnabled={lookupEnabled}
          >
            {field}
          </AssistAddons>
        ) : (
          field
        );
      })()}

      {/* Save button for clarification-enabled field edits */}
      {showClarificationSaveRow && (
        <div className="flex items-center gap-2 mt-1">
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={handleSaveEdit}
            disabled={isSavingEdit}
            className="text-xs"
          >
            <Pencil className="h-3 w-3 mr-1" />
            {isSavingEdit ? 'Saving...' : 'Save Edit'}
          </Button>
          {editSaved && <span className="text-xs text-green-600">Saved!</span>}
          {editError && (
            <span role="alert" className="text-xs text-red-600">
              {editError}
            </span>
          )}
        </div>
      )}

      {question.meta?.helpText && (
        <p className="text-xs text-gray-500">{question.meta.helpText}</p>
      )}

      {fieldError && (
        <p className="text-xs text-red-500">
          {fieldError.message || 'This field has an error'}
        </p>
      )}
    </div>
  );

  const questionBlock = isConditional ? (
    <div className="bg-gray-50 rounded-lg border border-gray-200 p-4 mt-4">
      {fieldContent}
    </div>
  ) : (
    fieldContent
  );

  // data-question-anchor is the clarification navigator's scroll target; it
  // toggles data-clarification-highlight on arrival for a brief ring.
  return (
    <div
      data-question-anchor={question.slug}
      className="flex items-start gap-3 rounded-lg ring-amber-400 ring-offset-4 transition-shadow duration-500 data-[clarification-highlight=true]:ring-2"
    >
      <div className="flex-1 min-w-0">{questionBlock}</div>
      {shouldShowClarificationIcon && (
        <div className="flex-shrink-0 pt-7 print:hidden">
          <ClarificationIcon
            submissionId={submissionId}
            questionSlug={question.slug}
            questionLabel={question.prompt}
          />
        </div>
      )}
    </div>
  );
};
